/**
 * CameraOrientationManager — the single authority for camera HEADING (the
 * user-facing screen orientation of an orientation class, see
 * personas/orientation.ts).
 *
 * Heading state has two layers:
 *
 * 1. **User heading** — the deliberate, persisted orientation per class
 *    (written only by explicit acts: alignment/orientation tools, or
 *    background room-orientation detection landing on a blank slate).
 *    Storage delegates to CameraSettingsManager, which the client already
 *    persists to the project database.
 *
 * 2. **Overrides** — an owner-keyed stack per class for SHORT-LIVED
 *    orientations (e.g. "align to this wall while the tool is focused").
 *    The top of the stack wins; releasing a non-top entry is bookkeeping
 *    only, so out-of-order release cannot corrupt anything; releasing the
 *    top re-applies the next effective heading to the live view. Handles
 *    are idempotent — a leaked or double release is safe and attributable
 *    to its owner.
 *
 * Free user rotation is deliberately NOT captured here (preserve-not-
 * restore): switches within one orientation class never touch the camera,
 * so the user's rotation simply survives; class entries apply
 * `effectiveHeading`, and only explicit tools change what that is.
 */

import { EventDispatcher } from '../EventDispatcher';
import type { Viewer } from '../viewer/viewer';
import type { View } from '../viewer/View';
import { CameraSettingsManager, CameraSettingType } from './CameraSettingsManager';
import { OrientationKey, headingAxis, orientationKeyForView } from './orientation';

/** Provenance of a heading write. */
export type HeadingSource = 'alignment_tool' | 'auto_orientation' | 'auto_detected' | 'auto_detected_from_room' | 'restored' | 'manual';

export enum CameraOrientationEvents {
  /** The effective heading for a key changed (user write, override push/pop). */
  HEADING_CHANGED = 'orientation_heading_changed',
}

export interface HeadingChangedEvent {
  type: CameraOrientationEvents.HEADING_CHANGED;
  key: OrientationKey;
  /** New effective heading, or undefined when the last source of heading for the key went away. */
  heading: number | undefined;
  source: HeadingSource | 'override';
}

/**
 * Handle for a pushed override. `release()` is idempotent; `update()` moves
 * the entry to the top of its stack (last writer wins among concurrent
 * owners) and re-applies when currently visible.
 */
export interface OverrideHandle {
  readonly owner: string;
  readonly key: OrientationKey;
  readonly active: boolean;
  update(heading: number): void;
  release(): void;
}

interface OverrideEntry {
  owner: string;
  heading: number;
}

export class CameraOrientationManager extends EventDispatcher {
  private readonly viewer: Viewer;
  private readonly settings: CameraSettingsManager;
  private readonly overrides = new Map<OrientationKey, OverrideEntry[]>();

  constructor(viewer: Viewer, settings: CameraSettingsManager) {
    super();
    this.viewer = viewer;
    this.settings = settings;
  }

  // ==================== reads (synchronous only) ====================

  /** Persisted user heading for a class, or undefined. Plane classes are ephemeral (never persisted). */
  userHeading(key: OrientationKey): number | undefined {
    const type = CameraOrientationManager.settingTypeForKey(key);
    return type !== null ? this.settings.getRotation(type) : undefined;
  }

  /** Top of the override stack, else the user heading, else undefined. */
  effectiveHeading(key: OrientationKey): number | undefined {
    const stack = this.overrides.get(key);
    if (stack && stack.length > 0) {
      return stack[stack.length - 1].heading;
    }
    return this.userHeading(key);
  }

  isOverridden(key: OrientationKey): boolean {
    return (this.overrides.get(key)?.length ?? 0) > 0;
  }

  /** Orientation key of the view currently shown on the main view, or null. */
  currentKey(): OrientationKey | null {
    return orientationKeyForView(this.viewer.personaManager?.viewManager.getCurrentView() ?? null);
  }

  // ==================== writes ====================

  /**
   * Deliberate persistent heading write (alignment/orientation tools).
   * Persists through CameraSettingsManager's existing ROTATION_CHANGED →
   * database chain, and applies to the live view when the class is
   * currently shown and not overridden.
   */
  setUserHeading(key: OrientationKey, heading: number, source: HeadingSource): void {
    const type = CameraOrientationManager.settingTypeForKey(key);
    if (type === null) return;
    this.settings.saveRotation(type, heading, source);
    if (!this.isOverridden(key)) {
      this.applyIfCurrent(key);
    }
    this.dispatchEvent({
      type: CameraOrientationEvents.HEADING_CHANGED,
      key,
      heading: this.effectiveHeading(key),
      source,
    });
  }

  /**
   * Write the effective heading onto the class's canonical axis of `view`:
   * top-down 2D → roll (yaw pinned to 0, killing the gimbal-degenerate
   * axis); perspective 3D → yaw (roll pinned to 0); plane/side 2D → roll.
   * An undefined heading leaves the view untouched — never a forced zero.
   */
  applyToView(view: View, key: OrientationKey): void {
    const heading = this.effectiveHeading(key);
    if (heading === undefined) return;
    if (headingAxis(key) === 'roll') {
      if (key === 'top_down_2d') {
        view.yaw = 0;
      }
      view.roll = heading;
    } else {
      view.yaw = heading;
      view.roll = 0;
    }
  }

  // ==================== overrides ====================

  /**
   * Push a short-lived heading override for `key`. Re-pushing by the same
   * owner replaces its entry and raises it to the top. The override applies
   * immediately when the class is currently shown; releasing re-applies
   * whatever is effective next (another override or the user heading).
   */
  pushOverride(owner: string, key: OrientationKey, heading: number): OverrideHandle {
    const stack = (this.overrides.get(key) ?? []).filter((e) => e.owner !== owner);
    const entry: OverrideEntry = { owner, heading };
    stack.push(entry);
    this.overrides.set(key, stack);
    this.applyIfCurrent(key);
    this.dispatchHeadingChanged(key);

    let released = false;
    return {
      owner,
      key,
      get active(): boolean {
        return !released;
      },
      update: (nextHeading: number): void => {
        if (released) return;
        entry.heading = nextHeading;
        this.raiseOverride(key, entry);
        this.applyIfCurrent(key);
        this.dispatchHeadingChanged(key);
      },
      release: (): void => {
        if (released) return;
        released = true;
        this.removeOverride(key, entry);
      },
    };
  }

  private raiseOverride(key: OrientationKey, entry: OverrideEntry): void {
    const stack = this.overrides.get(key);
    if (!stack) return;
    const index = stack.indexOf(entry);
    if (index === -1 || index === stack.length - 1) return;
    stack.splice(index, 1);
    stack.push(entry);
  }

  private removeOverride(key: OrientationKey, entry: OverrideEntry): void {
    const stack = this.overrides.get(key);
    if (!stack) return;
    const index = stack.indexOf(entry);
    if (index === -1) return;
    const wasTop = index === stack.length - 1;
    stack.splice(index, 1);
    if (stack.length === 0) {
      this.overrides.delete(key);
    }
    // Only a top removal changes the effective heading — position in the
    // stack, not release order, decides visibility (out-of-order safe).
    if (wasTop) {
      this.applyIfCurrent(key);
      this.dispatchHeadingChanged(key);
    }
  }

  // ==================== async detection (off the switch path) ====================

  /**
   * Kick background room-orientation detection when the top-down class has
   * no saved heading yet. Never awaited on a switch path — the result lands
   * via CameraSettingsManager's guarded safe-apply.
   */
  ensureDetectedHeading(): void {
    this.settings.ensureDetectedRotation();
  }

  // ==================== internals ====================

  private applyIfCurrent(key: OrientationKey): void {
    if (this.currentKey() !== key) return;
    this.applyToView(this.viewer.scene.view, key);
  }

  private dispatchHeadingChanged(key: OrientationKey): void {
    this.dispatchEvent({
      type: CameraOrientationEvents.HEADING_CHANGED,
      key,
      heading: this.effectiveHeading(key),
      source: 'override',
    });
  }

  /** Storage bucket behind a class; plane classes are ephemeral (null). */
  private static settingTypeForKey(key: OrientationKey): CameraSettingType | null {
    switch (key) {
      case 'top_down_2d':
        return CameraSettingType.ORTHOGRAPHIC_TOP;
      case 'side_2d':
        return CameraSettingType.ORTHOGRAPHIC_SIDE;
      case 'perspective_3d':
        return CameraSettingType.PERSPECTIVE_3D;
      default:
        return null;
    }
  }
}
